import { doc, onSnapshot } from 'firebase/firestore'
import { fetchStoreApi } from '@/lib/apiBase'
import { db, COLLECTIONS, isFirebaseConfigured } from '@/lib/firebase'
import type { PurchaseActivity } from '@/types'

export const PURCHASE_ACTIVITY_DOC_ID = 'latest'

export function purchaseActivityEventKey(activity: PurchaseActivity): string {
  if (activity.sequence != null) {
    return String(activity.sequence)
  }
  return `${activity.purchasedAt}:${activity.updatedAt || ''}`
}

function mapPurchaseActivity(data: Record<string, unknown>): PurchaseActivity | null {
  const productName = String(data.productName || '').trim()
  if (!productName) return null

  return {
    productName,
    productImage: String(data.productImage || '').trim(),
    variant:
      typeof data.variant === 'string' && data.variant.trim()
        ? data.variant.trim()
        : undefined,
    purchasedAt:
      typeof data.purchasedAt === 'string' && data.purchasedAt
        ? data.purchasedAt
        : new Date().toISOString(),
    updatedAt:
      typeof data.updatedAt === 'string' ? data.updatedAt : undefined,
    sequence:
      typeof data.sequence === 'number' && Number.isFinite(data.sequence)
        ? data.sequence
        : undefined,
  }
}

/** Load latest activity from API (seeds from recent orders when the doc is missing). */
export async function fetchPurchaseActivity(): Promise<PurchaseActivity | null> {
  try {
    const res = await fetchStoreApi('/api/store/purchase-activity')
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      activity?: unknown
    }
    if (!res.ok || data.ok === false || !data.activity) {
      return null
    }
    if (typeof data.activity !== 'object' || data.activity === null) {
      return null
    }
    return mapPurchaseActivity(data.activity as Record<string, unknown>)
  } catch {
    return null
  }
}

/** Checkout fallback when the Cloud Function has not published yet. */
export async function publishPurchaseActivityApi(
  orderId: string,
  contact: { email: string; phone: string }
): Promise<void> {
  const res = await fetchStoreApi('/api/store/purchase-activity/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      orderId,
      email: contact.email,
      phone: contact.phone,
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || 'Failed to publish purchase activity')
  }
}

/** Live listener for the latest sanitized purchase activity. */
export function subscribePurchaseActivity(
  onChange: (activity: PurchaseActivity | null) => void
): () => void {
  if (!isFirebaseConfigured()) {
    void fetchPurchaseActivity().then(onChange)
    return () => {}
  }

  const ref = doc(db, COLLECTIONS.purchaseActivity, PURCHASE_ACTIVITY_DOC_ID)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onChange(null)
        return
      }
      onChange(mapPurchaseActivity(snap.data() as Record<string, unknown>))
    },
    (error) => {
      if (import.meta.env.DEV) {
        console.warn('purchaseActivity listener error:', error)
      }
      void fetchPurchaseActivity().then(onChange)
    }
  )
}
